import SelectSearch from '@/components/react-hook-form-inputs/select-search/select-search-component';
import { BodyText, SubheaderText } from '@/utils/global-fonts';
import { requiredValidationMessage } from '@/utils/validations';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import Card from '../card/card-component';
import { Col, Row } from '../grid/grid-component';
import Input from '../react-hook-form-inputs/input/input-component';

const StyledBodyText = styled(BodyText)`
  color: ${({ theme }) => theme.COLOR_TEXT};
`;

const GreyLine = styled.div`
  display: flex;
  height: 1px;
  background-color: ${({ theme }) => theme.COLOR_GREY5};
`;

/**
 * Quote Detail component.
 * @param {object} props The component props.
 * @param {string} props.heading The heading of the component.
 * @param {string} props.description The description of the component.
 * @param {boolean} props.loading The loading state of the component.
 * @param {Function} props.onCustomValueChange The function to call when the custom value input changes.
 * @param {Function} props.dropdownData The data for the dropdown.
 * @param {string} props.name The name of the input for React Hook Form. (Required)
 * @returns {JSX.Element} The rendered Quote Detail component.
 */
const QuoteDetailDropdown = ({
  heading,
  description,
  loading,
  dropdownData,
  onCustomValueChange,
  name,
}) => {
  const { setValue, watch } = useFormContext();

  const options = dropdownData?.map((option) => ({
    value: option.value,
    label: option.value,
  }));

  const selectOption = (e) => {
    let selectedValue = e?.value;
    if (selectedValue === 'Other') {
      selectedValue = '';
    }
    setValue(`${name}.value`, selectedValue);
  };

  const customField = watch(`${name}.selected`);
  const isCustomField = customField === 'Other';

  return (
    <Card margin="8px 0px 8px 0px" borderRadius="4px">
      <SubheaderText>{heading}</SubheaderText>
      <GreyLine />
      {description && <StyledBodyText>{description}</StyledBodyText>}
      <Row $marginBottom="0 !important">
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          <SelectSearch
            header="Please Select"
            name={`${name}.selected`}
            label="Please select a status"
            options={options}
            isLoading={loading}
            onChange={selectOption}
          />
        </Col>
        <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
          {isCustomField && (
            <Input
              disabled={loading}
              label="Custom Value"
              placeholder="Custom Value"
              name={`${name}.value`}
              registerOptions={{
                required: requiredValidationMessage,
                onChange: onCustomValueChange,
              }}
            />
          )}
        </Col>
      </Row>
    </Card>
  );
};

QuoteDetailDropdown.propTypes = {
  heading: PropTypes.string,
  loading: PropTypes.bool,
  name: PropTypes.string.isRequired,
  dropdownData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  onCustomValueChange: PropTypes.func,
  description: PropTypes.string,
};

QuoteDetailDropdown.defaultProps = {
  heading: '',
  loading: false,
  dropdownData: [],
  onCustomValueChange: () => {},
  description: '',
};

export default QuoteDetailDropdown;
